// The `InterestFreeCredit` component is a React functional component that displays interest-free credit information with
// structured sections. It includes a hero banner, a "Shop Now" section with clickable images, an FAQ section using an
// accordion component, and terms & conditions rendered with markdown support. The component dynamically fetches data from the
// `interestFreeCredit` prop and ensures a responsive, user-friendly layout using Tailwind CSS. It enhances user engagement
// with hover effects and interactive FAQs.
// Shop now : Weight changed to 6000 -> font-semibold
// Shop now : font size and text to upper case

import React, { useState } from 'react';
import { InterestFAQ, InterestFreeCreditDataSource, ShopNowContent, TermsAndCondition } from 'types/next';
import Link from 'components/commercetools-ui/atoms/link';
import { MarkDownDescription } from 'components/commercetools-ui/atoms/markdown/markdown';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';

interface InterestFreeCreditProps {
  interestFreeCredit: InterestFreeCreditDataSource;
}

const InterestFreeCredit = ({ interestFreeCredit }: InterestFreeCreditProps) => {
  const heroBanner = interestFreeCredit?.heroBanner || '';
  const shopNow = interestFreeCredit?.shopNow || '';
  const faqs = interestFreeCredit?.faqs || '';
  const termsAndCondition = interestFreeCredit?.termsAndCondition || '';
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleReadMore = () => setIsExpanded(!isExpanded);

  return (
    <>
      <div className="hero-section mb-20 ">
        <img src={heroBanner} className="w-[100%]" />
      </div>
      <div className="mb-20 px-5 lg:mx-15">
        <div className="mb-10 flex items-center justify-center p-5 sm:p-20 lg:p-50">
          <h2 className="mb-10 font-body text-[27px] font-semibold sm:text-[30px] lg:text-[36px]">
            {shopNow?.overallHeading.toUpperCase()}
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
          {shopNow?.content &&
            shopNow?.content?.map((item, index) => (
              <a href={item?.link} key={index} className="group relative block overflow-hidden py-[8px]">
                {/* Image Container */}
                <div className="relative w-full">
                  <img
                    src={`https://americangolf.a.bigcontent.io/v1/static/${item?.image?.name}`}
                    className="w-full transition-all duration-500"
                    alt="Golf Clubs"
                  />
                  {/* Gradient that flows from bottom to top */}
                  <div className="absolute inset-0 translate-y-full bg-gradient-to-t from-black/80 to-transparent opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100" />
                </div>

                {/* Title moves down slightly on hover */}
                <h1
                  className="
    absolute inset-0 flex items-center justify-center text-center 
    text-[25px] font-bold text-white transition-all duration-300 group-hover:translate-y-1 
    sm:text-[18px] 
    md:left-[8%] md:top-[15%] 
    lg:left-0 lg:top-0 lg:text-[25px] 
  "
                >
                  {item?.title}
                </h1>

                {/* "Shop Now" moves up slightly on hover */}
                <div className="absolute bottom-0 left-0 right-0 translate-y-full py-4 text-center text-sm font-semibold text-white opacity-0 transition-all duration-500 group-hover:translate-y-[-50px] group-hover:opacity-100">
                  <p className="text-19">SHOP NOW</p>
                </div>
              </a>
            ))}
        </div>
      </div>
      <div className="faqs-section mb-20 px-[15px] pb-20 pt-15 md:mx-40 lg:mx-auto lg:w-[1000px] 2xl:w-[73rem]">
        <div className="flex justify-center">
          <p className="mb-[30px] font-body text-34 font-medium leading-6">{'FAQ'}</p>
        </div>
        <hr />
        {faqs &&
          faqs?.content?.map((faq: InterestFAQ, index: number) => {
            return (
              <AccordionBtn
                variant="plusAndMinus"
                className="insurancefaq group items-center border-b border-[#d1c9c4] text-16 font-semibold"
                closedSectionTitle={faq?.question}
                panelClassName="text-primary-black pt-10"
                key={index}
                iconClassName="faqicon text-black"
              >
                <MarkDownDescription
                  classname="faqans p-[15px] leading-[1.6] font-body font-normal"
                  markdownText={faq.answer}
                />
              </AccordionBtn>
            );
          })}
      </div>
      <div className="Terms-Condition-Section mb-20 flex flex-col items-center justify-center gap-y-10 md:mx-40 lg:mx-auto lg:w-[1000px] 2xl:w-[73rem]">
        <h1 className="mb-10 whitespace-nowrap text-center font-body text-[29px] font-bold sm:text-[33px] lg:text-[36px]">
          {'Terms & Conditions'}
        </h1>

        <MarkDownDescription
          markdownText={
            isExpanded
              ? termsAndCondition?.content
              : termsAndCondition?.content?.split(' ').slice(0, 100).join(' ') + '...'
          }
          classname="px-[15px]"
        />

        {termsAndCondition?.content?.length > 400 && (
          <button
            onClick={toggleReadMore}
            className="group relative mt-4 flex items-center px-6 py-2 text-black transition-all duration-500"
          >
            <span className="text-base">{isExpanded ? 'Read Less' : 'Read More'}</span>
            <span className="ml-6 text-3xl font-semibold transition-transform duration-300">
              {isExpanded ? '-' : '+'}
            </span>

            {/* SVG Animated Border */}
            <svg className="absolute inset-0 h-full w-full">
              <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                rx="5" /* Rounded corners */
                ry="5"
                fill="none"
                stroke="black"
                stroke-width="1"
                stroke-dasharray="300, 900"
                stroke-dashoffset="300"
                className="group-hover:stroke-dashoffset-0 transition-all duration-700 group-hover:stroke-black"
              />
            </svg>

            {/* Internal Styles for Better Animation */}
            <style>{`
            button svg rect {
              transition: stroke-dashoffset 0.7s ease-in-out;
            }
            .group:hover svg rect {
              stroke-dashoffset: 0;
            }
          `}</style>
          </button>
        )}
      </div>
    </>
  );
};

export default InterestFreeCredit;
